import React, { useEffect, useState } from 'react';
import { usePortal } from '../contexts/PortalContext';
import { Home } from './portal/Home';
import { Projects } from './portal/Projects';
import { ProjectAdmin } from './portal/ProjectAdmin';
import { ShowAdmin } from './portal/ShowAdmin';
import { Team } from './portal/Team';
import { Settings } from './portal/Settings';
import { Calendar } from './portal/Calendar';
import { News } from './portal/News';
import { CallSheets } from './portal/CallSheets';
import { WrapUps } from './portal/WrapUps';
import { Conversations } from './portal/Conversations';
import { StaffAccount } from '../components/portal/StaffAccount';
import { StudioNav, StudioTabs } from '../components/portal/StudioNav';
import { UpdateBar } from '../components/portal/UpdateBar';
import { installStandaloneApp } from '../utils/homeScreen';
import { useNewVersionReady } from '../utils/appVersion';
import { useStudioViewHistory, type StudioView } from '../utils/studioViewHistory';

/**
 * Studio-wide views are held here rather than in `portalRoute`.
 *
 * Each is a view ACROSS every project rather than a step in the project/show
 * flow the route state models, so keeping them out of that state means they
 * cannot strand someone mid-edit on another screen.
 *
 * `useStudioViewHistory` is what stops that separation costing the back
 * button: it writes the open view onto the same history entry PortalContext
 * maintains, so back steps out of Calendar to wherever you were rather than
 * out of Portal OS altogether.
 *
 * **23 Sep 2026: the front page is now the week, not the clients.** 'none'
 * means Home — and the client list moved to its own 'clients' view.
 *
 * 'clients' has to behave like 'none' for everything below the list, because
 * opening a client from it sets `portalRoute` and leaves the view where it
 * was.
 *
 * **24 Sep 2026: four of these views, as a bottom bar on a phone.** The nav
 * row wrapped to four stacked lines at 390px. `StudioTabs` is that row,
 * thumb-height; `StudioNav` keeps the full set on a laptop.
 *
 * **27 Sep 2026: which four changed** — Week, News, Dispatches, Calendar,
 * with Call Sheets and Clients moving into the Menu sheet.
 *
 * **25 Sep 2026: 'conversations'**, owners only — see the guard below, and
 * read the note there before assuming this file is what enforces it.
 *
 * **26 Sep 2026: 'wrapUps'.** The post-show reports, a view across every
 * project rather than a section on Project Admin, where Tom could not find
 * it.
 *
 * **4 Nov 2026: the front page's tiles route through here too.** `Home`
 * takes `onSelectView`, `onNewProject`, `onOpenSettings` and `isOwner` so
 * the ten squares can act. The page still owns no navigation of its own —
 * it is handed the same `selectView` the nav row uses, so there remains
 * exactly one place that decides which view is open.
 *
 * **5 Nov 2026: 'settings', and the front page header is the wordmark
 * alone.** The tiles carry the eight views, New Project and the account, so
 * none of them is drawn in the header there — see `showTabs` in StudioNav
 * for why that is the front page only. 'settings' is the screen the tile
 * had been promising: Team, the signed-in account and worker health.
 */

/**
 * **THE FRONT PAGE'S GROUND. 5 Nov 2026, and it belongs to that page only.**
 *
 * Two things from Portal's brand guidelines that the studio screens had
 * never honoured: the brand's dark is a warm charcoal rather than the
 * near-black `ink` this app was built on, and a gradient is one of the
 * brand's own graphic elements.
 *
 * It matters on the front page because that page is ten bright squares: a
 * flat black field behind them reads as a hole, and a ground with a light
 * direction in it reads as a room. It does not matter anywhere else, and
 * Tom asked for it nowhere else — a project page is a document and wants a
 * plain ground behind it. So it is applied only while Home is the thing on
 * screen; every other view keeps `ink`.
 *
 * **#262324 is the brightest point, not the base** (Tom, 5 Nov: "darker —
 * the darkest brand colour without it being full black"). Everything else
 * falls away from it to #141313. Raising any of these numbers undoes that.
 *
 * **THE LIGHT COMES FROM THE BOTTOM RIGHT, AND IT IS NOT AN AESTHETIC
 * CHOICE** (Tom, 5 Nov: "the background colour and settings colour is very
 * similar — shall we have the gradient start lighter in bottom right"). It
 * ran the other way at first, lightest top left, which put the darkest
 * corner of the page — #121111 — directly behind the Settings tile at
 * #121112. One value apart. The tile had no edge against the page it sat
 * on, and the whole bottom-right corner of the screen turned to mush.
 *
 * Turning the light round fixes it twice over: the drawn tiles now have the
 * brightest ground in the page to sit against, and the field gets darker as
 * it rises towards the solid ink nav band, so the band reads as the top of
 * the room rather than a stripe laid across it.
 *
 * **So the rule is: the ground is lightest where the darkest panel sits.**
 * If the drawn tiles ever move out of the bottom right, this moves too.
 *
 * **It is on the SCROLLER, not on the shell, and that is what keeps the
 * black banner** (Tom, 5 Nov: "where is the black banner at the top?"). The
 * shell stays `ink`, so the nav sits on a solid black band and the gradient
 * begins underneath it — the hard horizontal edge in the mock-up. Move this
 * style up onto the shell and the banner dissolves into the gradient again.
 */
const FRONT_PAGE_GROUND: React.CSSProperties = {
  backgroundColor: '#1C1A1B',
  backgroundImage: ['radial-gradient(110% 80% at 94% 112%, rgba(109,110,113,0.22) 0%, rgba(109,110,113,0) 58%)', 'linear-gradient(to top left, #262324 0%, #1D1B1C 48%, #141313 100%)'].join(', ')
};

/**
 * **THE SHELL IS A FIXED COLUMN ON A PHONE (27 Sep 2026). Do not put the
 * scrolling back on the document.**
 *
 * The tab bar used to be `position: fixed; bottom: 0` over a document that
 * scrolled. On Tom's installed iPhone app the first touch of every session
 * opened the Calendar whichever tab he pressed, and a probe on his own phone
 * showed why:
 *
 *   t 124,789 hit Wed 30Clear · bar 816-911 root 59-1223 body 59 ·
 *   vh 852 vv 852@-59 100vh 852 100dvh 852 · sc -59 ob none
 *
 * The document was sitting at **scrollY = -59** — body top 59, shell top 59,
 * visual-viewport offset -59, all the same displacement, and 59 is exactly
 * the status-bar inset on that device. iOS carries `position: fixed` with
 * that displacement, so the bar's rect was 816–911 against an 852-tall
 * screen: its bottom 59px hanging off the bottom, and the strip where the
 * labels appear at rest now occupied by the last Week Ahead day row — a
 * full-width button whose one job is to open the Calendar. Hence "every tab
 * jumps to calendar".
 *
 * Two fixes were tried against the symptom and both missed, because both
 * assumed a cause: a different tab set, then `overscroll-behavior: none`
 * (which the probe confirmed was live, and changed nothing — the offset is
 * not a settling bounce). **So this stops depending on the behaviour
 * instead of trying to correct it.**
 *
 * Below `xl` the shell is a fixed-height flex column: `.studio-shell` in
 * index.css sets `100dvh`, `overflow-hidden` keeps the document from
 * scrolling at all, header and tab bar are ordinary rows, and there is ONE
 * scroller in the middle. A document that cannot scroll cannot be parked at
 * a negative offset, and a bar that is laid out rather than positioned has
 * nothing to be dragged by. From `xl` up everything reverts to ordinary
 * document flow — the laptop had no bug and got no change.
 *
 * ---
 *
 * **EXCEPT THE FRONT PAGE, WHICH DOES NOT SCROLL AT ALL (Tom, 5 Nov: "the
 * page is STILL scrolling, I don't want that, make sure it is a static
 * page").**
 *
 * On a laptop, Home holds the viewport exactly: the shell is pinned to
 * `100dvh` by an inline height — inline, because `.studio-shell` in
 * index.css already owns that property below xl and two stylesheet rules of
 * equal weight would have the answer decided by file order — and the middle
 * scroller is sealed from xl up.
 *
 * **A sealed scroller clips whatever does not fit**, so this only works
 * because Home is built to fill rather than to stack: see the note there.
 * Anything added to that page has to come out of something else — which is
 * why the nav row leaving it is worth real height, not just tidiness. Below
 * xl the scroller stays open, because ten tiles two-up on a phone is a
 * column no viewport will ever hold.
 */
export function PortalApp() {
  const {
    portalRoute,
    goPortal,
    staffUser,
    staffSignOut
  } = usePortal();
  const [accountOpen, setAccountOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [view, setView] = useState<StudioView>('none');
  /**
   * The day the Calendar should open on, when it was opened by pressing a
   * day somewhere else (24 Sep 2026 — the Week Ahead rows).
   *
   * It lives here rather than in Calendar because Calendar is unmounted the
   * moment you leave the tab, so there is nowhere in it for an intention
   * formed on another screen to wait.
   */
  const [calendarDay, setCalendarDay] = useState('');
  const isOwner = staffUser?.role === 'owner';
  useStudioViewHistory(view, setView);

  /**
   * Nobody who is not an owner can be left sitting on Dispatches.
   *
   * `useStudioViewHistory` restores whatever view you last had open, so
   * somebody who could reach Dispatches yesterday would come back to it by
   * history alone with no button ever pressed.
   *
   * **NONE OF THIS IS THE SECURITY BOUNDARY.** The real locks are
   * `is_portal_owner()` on every conversation_* RPC, and the same check
   * inside the ask-dispatches Edge Function — which matters most, because
   * that one holds a domain-wide Gmail token and does not go through the
   * database at all. This effect is courtesy, not defence.
   *
   * Settings is deliberately NOT guarded the same way: it is the account
   * screen as well as the team screen, so everyone needs it. The Team
   * section inside it shows non-owners a line saying so, and the Edge
   * Function behind the list refuses them regardless.
   */
  useEffect(() => {
    if (!isOwner && view === 'conversations') setView('none');
  }, [isOwner, view]);

  /* Leaving the Calendar drops the focused day, so coming back later opens an
     ordinary calendar rather than one still pointing at a day you dealt with
     ten minutes ago. */
  useEffect(() => {
    if (view !== 'calendar') setCalendarDay('');
  }, [view]);

  /**
   * Staff side only, both of these.
   *
   * `installStandaloneApp` — opening without browser chrome, and the install
   * manifest. The ICON is installed from App.tsx instead, at boot, because a
   * browser reads a page's icons on load and this component only mounts once
   * you are signed in.
   *
   * `useNewVersionReady` — whether Portal OS has been published since this
   * window opened. A client opens a review link fresh each time and never
   * needs telling; the people who keep it open all day do.
   */
  useEffect(() => {
    void installStandaloneApp();
  }, []);
  const updateReady = useNewVersionReady();

  /** The way out of anywhere. The wordmark is where everyone clicks first. */
  function goHome() {
    setView('none');
    setCreating(false);
    goPortal('projects');
  }

  /* The old project-scoped route still exists because ProjectAdmin's own
     button uses it. It now opens the studio-wide list rather than that one
     project's single sheet. */
  const showingCallSheets = view === 'callSheets' || view === 'none' && portalRoute === 'callSheet';

  /* Home and Clients share the project/show flow underneath them. */
  const inProjectFlow = !showingCallSheets && (view === 'none' || view === 'clients');

  /* The one condition under which <Home /> is what you are looking at. The
     ground, the no-scroll rule and the bare header all hang off it, so it is
     derived once here rather than re-tested in three places that could drift
     apart. */
  const showingHome = inProjectFlow && view === 'none' && portalRoute === 'projects';
  function selectView(next: StudioView) {
    setCreating(false);
    setView(next);
    if (portalRoute !== 'projects') goPortal('projects');
  }

  /** One definition, used by the nav row, the phone sheet and the tile. */
  function startNewProject() {
    /* A new project belongs on the list it joins, not on the front page. */
    setView('clients');
    goPortal('projects');
    setCreating(true);
  }

  /** The Settings screen, from the front page's tile. */
  function openSettings() {
    selectView('settings');
  }

  /** The Team screen on its own, from the laptop nav row. */
  function openTeam() {
    setView('none');
    goPortal('team');
  }

  /** Pressing a day on the Week Ahead: the Calendar, opened on that day. */
  function openCalendarOn(iso: string) {
    setCreating(false);
    setCalendarDay(iso);
    setView('calendar');
    if (portalRoute !== 'projects') goPortal('projects');
  }
  return <div style={showingHome ? {
    height: '100dvh'
  } : undefined} className={`studio-shell flex w-full flex-col overflow-hidden bg-ink text-paper ${showingHome ? '' : 'xl:block xl:min-h-screen xl:overflow-visible'}`} data-id="element-2617" data-mp="2gqtpibz27m9j">
      {updateReady && <UpdateBar data-id="element-2618" data-mp="1t2tqkorlgpjv" />}

      {/* On ink, always. The band across the top of the front page is this
          element sitting on the shell's own background while the gradient
          starts below it — see FRONT_PAGE_GROUND. On the front page it is
          the wordmark alone: the tiles carry the views, New Project and the
          account. */}
      <StudioNav view={view} showingCallSheets={showingCallSheets} isOwner={isOwner} name={staffUser?.name ?? ''} showTabs={!showingHome} onGoHome={goHome} onNewProject={startNewProject} onSelectView={selectView} onOpenTeam={openTeam} onOpenAccount={() => setAccountOpen(true)} onSignOut={staffSignOut} data-id="element-2619" data-mp="1a4pogea4jqrk" />

      {accountOpen && <StaffAccount onClose={() => setAccountOpen(false)} data-id="element-2620" data-mp="xctb3d8p2vh8" />}

      {/* THE ONLY SCROLLER below xl. `min-h-0` is what lets a flex child
          actually shrink and scroll rather than growing to fit its content.
          `overscroll-contain` keeps a flick at the end of a list from
          chaining outwards. Above xl it is an ordinary div and the document
          scrolls as it always did — unless this is the front page, which is
          sealed, and which is also where the gradient lives. */}
      <div style={showingHome ? FRONT_PAGE_GROUND : undefined} className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${showingHome ? 'xl:overflow-hidden' : 'xl:flex-none xl:overflow-visible'}`} data-id="element-2621" data-mp="9fw3nxm3dbzu">
        <main className={`mx-auto max-w-portal px-6 pt-8 sm:px-10 sm:pt-10 ${showingHome ? 'flex h-full flex-col pb-8' : 'pb-16 xl:pb-20'}`} data-id="element-2622" data-mp="23u6nfrntxfyr">
          {showingCallSheets && <CallSheets data-id="element-2623" data-mp="2g9wkyf3jbt0w" />}
          {!showingCallSheets && view === 'calendar' && <Calendar focusDate={calendarDay} data-id="element-2624" data-mp="2l40tv3wd2b4m" />}
          {!showingCallSheets && view === 'wrapUps' && <WrapUps data-id="element-2625" data-mp="2q0hidz7h2ri8" />}
          {!showingCallSheets && view === 'conversations' && isOwner && <Conversations data-id="element-2626" data-mp="1tcbkrw8ywzdg" />}
          {!showingCallSheets && view === 'news' && <News data-id="element-2627" data-mp="1xd59usy5ck7x" />}
          {!showingCallSheets && view === 'settings' && <Settings onOpenAccount={() => setAccountOpen(true)} onSignOut={staffSignOut} data-id="element-2628" data-mp="1xf7xj6btu19h" />}
          {inProjectFlow && <>
              {portalRoute === 'projects' && (view === 'clients' ? <Projects creating={creating} setCreating={setCreating} data-id="element-2629" data-mp="3drws4d1kwb3i" /> : <Home onOpenDay={openCalendarOn} onSelectView={selectView} onNewProject={startNewProject} onOpenSettings={openSettings} isOwner={isOwner} data-id="element-2630" data-mp="3ipyh1elor4mw" />)}
              {portalRoute === 'project' && <ProjectAdmin data-id="element-2631" data-mp="32z787ozo4yo8" />}
              {portalRoute === 'show' && <ShowAdmin data-id="element-2632" data-mp="2y1feqqjdl4hx" />}
              {portalRoute === 'team' && <Team data-id="element-2633" data-mp="2y1ymxfj84eir" />}
            </>}
        </main>
      </div>

      <StudioTabs view={view} showingCallSheets={showingCallSheets} isOwner={isOwner} onSelectView={selectView} data-id="element-2634" data-mp="2acw96irpn5tj" />
    </div>;
}